import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useFormatting } from "@msys/ui";
import { Link, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { isNil } from "lodash-es";
import React, { Dispatch, SetStateAction } from "react";
import {
  OrganisationQuoteTemplatesFilters,
  ProjectType,
  TypeOfTemplate,
} from "../../../../clients/graphqlTypes.js";
import {
  FilterChip,
  FilterChipGroup,
} from "../../../commons/filters/FilterChip.js";
import { useCategories } from "../../categories/useCategories.js";
import { useProjectTypes } from "../../projects/useProjectTypes.js";
import { PropertiesFilterChips } from "../../properties/PropertiesFilterChips.js";
import { TemplateFiltersFormValues } from "../filters/TemplateFilterFields.js";
import { useTemplatesQuoteFilterChips_TemplateTypesQuery } from "./TemplatesQuoteFilterChips.generated.js";
import { useTypeOfTemplates } from "./useTypeOfTemplates.js";

type Filters = Pick<
  OrganisationQuoteTemplatesFilters,
  | "createdAfter"
  | "createdBefore"
  | "priceMin"
  | "priceMax"
  | "tagsAny"
  | "tagsAll"
>;

interface Props<F extends Filters> {
  filters: F;
  setFilters: Dispatch<SetStateAction<F>>;
  templateFilters?: TemplateFiltersFormValues;
  setTemplateFilters?: Dispatch<SetStateAction<TemplateFiltersFormValues>>;
  onReset?(): void | Promise<void>;
  fixedFilters?: Partial<F> & Partial<TemplateFiltersFormValues>;
}

export const TemplatesQuoteFilterChips = <F extends Filters>({
  filters,
  setFilters,
  templateFilters,
  setTemplateFilters,
  fixedFilters,
  onReset,
}: Props<F>) => {
  const { t } = useTranslate(["Templates", "Global"]);
  const { getFormattedDate, getFormattedPrice } = useFormatting();

  const { projectTypeLabels } = useProjectTypes();
  const { typeOfTemplatesLabels } = useTypeOfTemplates();
  const { resolveCategories } = useCategories();

  const client = useApolloClient();
  const templateTypesQuery = useTemplatesQuoteFilterChips_TemplateTypesQuery({
    client,
    variables: {
      ids: templateFilters?.templateTypeIds ?? [],
    },
    fetchPolicy: "cache-and-network",
    skip:
      !templateFilters?.templateTypeIds ||
      templateFilters.templateTypeIds.length === 0,
  });

  const templateTypes = React.useMemo(
    () =>
      getDataOrNull(
        (templateTypesQuery.data ?? templateTypesQuery.previousData)
          ?.templateTypes
      )?.templateTypes ?? [],
    [templateTypesQuery.data, templateTypesQuery.previousData]
  );

  const categories = React.useMemo(
    () =>
      !templateFilters?.categories || templateFilters.categories.length === 0
        ? []
        : resolveCategories(templateFilters.categories, {
            addParentsWhenNotIncluded: false,
            removeChildrenIfIncluded: false,
            excludeDeletedCategories: true,
          }),
    [resolveCategories, templateFilters?.categories]
  );

  const getTemplateTypeTitle = (id: string) =>
    templateTypes.find(templateType => templateType.id === id)?.title;

  const getCategoryTitle = (id: string) =>
    categories.find(c => c.id === id)?.name;

  const getTypeOfProjectTitle = (value: ProjectType) =>
    projectTypeLabels[value];

  const getTypeOfTemplateTitle = (value: TypeOfTemplate) =>
    typeOfTemplatesLabels[value];

  const getTemplateTypePropertyTitle = (key: string) => {
    for (const templateType of templateTypes) {
      const prop = templateType.props2.find(p => p.key === key);
      if (prop) return prop.label;
    }
  };

  return canShowChips(filters, templateFilters) ? (
    <Stack
      direction={"row"}
      spacing={1}
      flexWrap="wrap"
      alignItems="center"
      useFlexGap
    >
      {templateFilters?.templateTypeIds &&
        templateFilters.templateTypeIds.length > 0 && (
          <FilterChipGroup
            values={templateFilters.templateTypeIds}
            setValues={newValues => {
              setTemplateFilters?.(filters => ({
                ...filters,
                templateTypeIds:
                  newValues && newValues.length > 0 ? newValues : null,
              }));
            }}
            getValueLabel={getTemplateTypeTitle}
            canEdit={isNil(fixedFilters?.templateTypeIds)}
          />
        )}
      {templateFilters?.categories && templateFilters.categories.length > 0 && (
        <FilterChipGroup
          values={templateFilters.categories}
          setValues={newValues => {
            setTemplateFilters?.(filters => ({
              ...filters,
              categories: newValues && newValues.length > 0 ? newValues : null,
            }));
          }}
          getValueLabel={getCategoryTitle}
          canEdit={isNil(fixedFilters?.categories)}
        />
      )}
      {templateFilters?.typeOfProjects &&
        templateFilters.typeOfProjects.length > 0 && (
          <FilterChipGroup<ProjectType>
            values={templateFilters.typeOfProjects}
            setValues={newValues => {
              setTemplateFilters?.(filters => ({
                ...filters,
                typeOfProjects:
                  newValues && newValues.length > 0 ? newValues : null,
              }));
            }}
            getValueLabel={getTypeOfProjectTitle}
            canEdit={isNil(fixedFilters?.typeOfProjects)}
          />
        )}
      {templateFilters?.typeOfTemplates &&
        templateFilters.typeOfTemplates.length > 0 && (
          <FilterChipGroup<TypeOfTemplate>
            values={templateFilters.typeOfTemplates}
            setValues={newValues => {
              setTemplateFilters?.(filters => ({
                ...filters,
                typeOfTemplates:
                  newValues && newValues.length > 0 ? newValues : null,
              }));
            }}
            getValueLabel={getTypeOfTemplateTitle}
            canEdit={isNil(fixedFilters?.typeOfTemplates)}
          />
        )}
      <FilterChip
        label={t("Created after")}
        resetValue={null}
        setValue={newValue =>
          setFilters(filters => ({
            ...filters,
            createdAfter: newValue,
          }))
        }
        value={filters.createdAfter && getFormattedDate(filters.createdAfter)}
        canEdit={isNil(fixedFilters?.createdAfter)}
      />
      <FilterChip
        label={t("Created before")}
        resetValue={null}
        setValue={newValue =>
          setFilters(filters => ({
            ...filters,
            createdBefore: newValue,
          }))
        }
        value={filters.createdBefore && getFormattedDate(filters.createdBefore)}
        canEdit={isNil(fixedFilters?.createdBefore)}
      />
      <FilterChip
        label={t("Min price")}
        resetValue={null}
        setValue={newValue =>
          setFilters(filters => ({
            ...filters,
            priceMin: newValue,
          }))
        }
        value={filters.priceMin}
        canEdit={isNil(fixedFilters?.priceMin)}
        getValueLabel={value => getFormattedPrice(value) ?? ""}
      />
      <FilterChip
        label={t("Max price")}
        resetValue={null}
        setValue={newValue =>
          setFilters(filters => ({
            ...filters,
            priceMax: newValue,
          }))
        }
        value={filters.priceMax}
        canEdit={isNil(fixedFilters?.priceMax)}
        getValueLabel={value => getFormattedPrice(value) ?? ""}
      />
      <FilterChipGroup
        label={t("Tag", { ns: "Templates" })}
        values={filters.tagsAny}
        getValueLabel={value => value}
        setValues={value =>
          setFilters(state => ({
            ...state,
            tagsAny: value,
          }))
        }
        canEdit={isNil(fixedFilters?.tagsAny)}
      />
      <FilterChip
        label={t("Tags (all)", { ns: "Templates" })}
        resetValue={null}
        setValue={newValue =>
          setFilters(filters => ({
            ...filters,
            tagsAll: newValue,
          }))
        }
        value={filters.tagsAll?.join?.(",")}
        canEdit={isNil(fixedFilters?.tagsAll)}
        getValueLabel={value => value}
      />
      {templateFilters?.properties && (
        <PropertiesFilterChips
          values={templateFilters?.properties}
          setValues={newValue =>
            setTemplateFilters?.(filters => ({
              ...filters,
              properties: newValue ?? null,
            }))
          }
          canEdit={isNil(fixedFilters?.properties)}
          getKeyLabel={getTemplateTypePropertyTitle}
        />
      )}
      {onReset && canResetFilters(filters, templateFilters, fixedFilters) && (
        <Typography variant="body2">
          <Link onClick={onReset} color="secondary" sx={{ cursor: "pointer" }}>
            {t("Clear all", { ns: "Global" })}
          </Link>
        </Typography>
      )}
    </Stack>
  ) : null;
};

export const canShowChips = (
  filters: Filters,
  templateFilters?: TemplateFiltersFormValues
): boolean =>
  Boolean(
    !isNil(filters.priceMin) ||
      !isNil(filters.priceMax) ||
      !isNil(filters.createdAfter) ||
      !isNil(filters.createdBefore) ||
      filters.tagsAny?.length ||
      filters.tagsAll?.length ||
      templateFilters?.templateTypeIds?.length ||
      templateFilters?.categories?.length ||
      templateFilters?.typeOfProjects?.length ||
      templateFilters?.typeOfTemplates?.length ||
      templateFilters?.properties?.length
  );

export const canResetFilters = (
  filters: Filters,
  templateFilters?: TemplateFiltersFormValues,
  fixedFilters?: Partial<Filters> & Partial<TemplateFiltersFormValues>
): boolean =>
  Boolean(
    (!isNil(filters.priceMin) && isNil(fixedFilters?.priceMin)) ||
      (!isNil(filters.priceMax) && isNil(fixedFilters?.priceMax)) ||
      (!isNil(filters.createdAfter) && isNil(fixedFilters?.createdAfter)) ||
      (!isNil(filters.createdBefore) && isNil(fixedFilters?.createdBefore)) ||
      (filters.tagsAny?.length && isNil(fixedFilters?.tagsAny)) ||
      (filters.tagsAll?.length && isNil(fixedFilters?.tagsAll)) ||
      (templateFilters?.templateTypeIds?.length &&
        isNil(fixedFilters?.templateTypeIds)) ||
      (templateFilters?.categories?.length &&
        isNil(fixedFilters?.categories)) ||
      (templateFilters?.typeOfProjects?.length &&
        isNil(fixedFilters?.typeOfProjects)) ||
      (templateFilters?.typeOfTemplates?.length &&
        isNil(fixedFilters?.typeOfTemplates)) ||
      (templateFilters?.properties?.length && isNil(fixedFilters?.properties))
  );
